import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { installationAPI } from '../api/installation';
import { toast } from 'react-toastify';
import './EditInstallation.css';

const EditInstallation = () => {
  const navigate = useNavigate();
  const { uniqueId } = useParams();
  
  const [formData, setFormData] = useState({
    installationName: '',
    description: '',
    computerName: '',
    isActive: true
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchInstallation();
  }, [uniqueId]);

  const fetchInstallation = async () => {
    try {
      setLoading(true);
      const response = await installationAPI.getByUniqueId(uniqueId);
      const installation = response.data.installation;
      setFormData({
        installationName: installation.installationName || '',
        description: installation.description || '',
        computerName: installation.computerName || '',
        isActive: installation.isActive !== undefined ? installation.isActive : true
      });
    } catch (error) {
      toast.error('Failed to fetch installation details');
      navigate('/installations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await installationAPI.update(uniqueId, formData);
      toast.success('Installation updated successfully!');
      navigate('/installations');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update installation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-installation-container">
        <div className="loading">Loading installation details...</div>
      </div>
    );
  }

  return (
    <div className="edit-installation-container">
      <div className="page-header">
        <div>
          <h1>Edit Installation</h1>
          <p>Update installation details and settings</p>
        </div>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit} className="installation-form">
          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="installationName">
                Installation Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="installationName"
                name="installationName"
                value={formData.installationName}
                onChange={handleChange}
                required
                minLength={3}
                maxLength={100}
                placeholder="e.g., Production Server, Development System"
                className="form-input"
              />
              <small className="help-text">
                A unique name to identify this installation
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="computerName">Computer Name</label>
              <input
                type="text"
                id="computerName"
                name="computerName"
                value={formData.computerName}
                onChange={handleChange}
                maxLength={100}
                placeholder="e.g., SERVER-01, DESKTOP-ABC123"
                className="form-input"
              />
              <small className="help-text">
                The name of the computer where the backup software is installed
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                maxLength={500}
                placeholder="Optional description about this installation"
                className="form-textarea"
              />
              <small className="help-text">
                Provide additional details about this installation
              </small>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/installations')}
              className="btn btn-secondary"
              disabled={submitting}
            >
              <span>✕</span> Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              <span>{submitting ? '⏳' : '✓'}</span>
              {submitting ? 'Updating...' : 'Update Installation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInstallation;